import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton.tsx';
import type { Account } from '@openx/types/account.ts';
import type { FiltersConfig } from '@openx/types/chipFilters.ts';
import type { ConstValues } from '@openx/types/helpers.ts';
import type { Option, OptionState } from '@openx/types/options.ts';
import type { Columns } from '@openx/types/tableHelpers.ts';
import type { OxsUserRoles, User, UserList } from '@openx/types/user.ts';

interface UseUsersListFetchResponse {
  error?: Error;
  loading: boolean;
  users: UserList[];
}

type UsersListFetchProps = {
  onCompleted?: (count: number | undefined) => void;
};

export type UseUsersListFetch = ({ onCompleted }: UsersListFetchProps) => UseUsersListFetchResponse;

export type UserPayload = Pick<UserList, 'first_name' | 'last_name' | 'email_address'> & {
  parent_account_id: string;
  role?: OxsUserRoles;
};

export const CreateUserMutationType = {
  PARENT: 'parent',
  ROOT: 'root',
} as const;
export type CreateUserMutationTypes = ConstValues<typeof CreateUserMutationType>;

export type CreateUserProps = {
  type: CreateUserMutationTypes;
  user: UserPayload;
  shouldRedirect?: boolean;
  onSuccess?: () => void;
};

export type UseUserCreate = () => {
  createUser: (props: CreateUserProps) => Promise<void>;
};

export type UserUpdatePayload = { data: Pick<UserList, 'first_name' | 'last_name'>; id: string };

export type UseEditUser = () => {
  editUser: (payload: UserUpdatePayload) => Promise<void>;
};

export type UseGetUsersTableData = (
  accounts: Account[],
  searchPhrase?: string
) => {
  columns: Columns<UserList>;
  filtersConfig: FiltersConfig;
  rowActions;
};

export type OptionWithUid = Option & { uid?: string };

export type UseAccountOptionsFetch = (skip?: boolean) => {
  accounts: OptionState<OptionWithUid>;
};

export type UserData = Pick<User, 'first_name' | 'last_name'> & {
  role: string;
  account: {
    id: string;
    name: string;
  } | null;
  email_address: string;
};

type UserFetchResponse = {
  user?: UserData | null;
  loading: boolean;
  refetch: () => void;
};

export type UseUserFetch = (userId?: string) => UserFetchResponse;

type UserPageHeaderActionsProps = {
  id?: string;
  name?: string;
};

export type UseUserPageHeaderActions = ({ id, name }: UserPageHeaderActionsProps) => ActionButtonProps[];

export type UseUserDeleteModal = ({
  shouldRedirect,
}: {
  shouldRedirect: boolean;
}) => ({ id, name }: { id: string; name: string }) => void;
